import { useState, useCallback, useEffect } from 'react'
import { iFullStoreState, UserAuth } from '../../../shared/interfaces'
import { ILoanerRecord, IMinimalDevice } from '../LoanerVehicles'

import { getAssignee } from '../utils/getAssignee'
import { addLoaner, deleteLoaner, getLoaners, updateLoaner } from '../shared/loaners-db'

import { useSelector } from 'react-redux'
import moment from 'moment-timezone'

export const useLoaners = () => {
  // Get auth user for loaner creation
  const authUser = useSelector<iFullStoreState, UserAuth | undefined>(
    (s) => s.auth?.user ?? undefined
  )

  const [activeLoaners, setActiveLoaners] = useState<ILoanerRecord[]>([])
  const [closedLoaners, setClosedLoaners] = useState<ILoanerRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: 'delete' | 'reopen' | 'deleteClosed' | null;
    loanerId: string | null;
  }>({
    open: false,
    action: null,
    loanerId: null,
  });

  // Fetch loaners from DB
  useEffect(() => {
    const fetchLoaners = async () => {
      if (!authUser) return;

      setIsLoading(true);

      try {
        const loanersFromDb: Record<string, ILoanerRecord> = await getLoaners();

        const loanersArray: ILoanerRecord[] = Object.values(loanersFromDb).map(
          (loaner) => ({
            ...loaner,
            isEditing: false,
          })
        );

        const active = loanersArray.filter((l) => !l.closedDate);
        const closed = loanersArray.filter((l) => l.closedDate);

        setActiveLoaners(active);
        setClosedLoaners(closed);
      } catch (error) {
        console.error('Failed to fetch loaners:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLoaners();
  }, [authUser]);

  const formatDate = useCallback((date: Date) => {
    return moment(date).format('MM-DD-YYYY');
  }, []);

	// Add new loaner
  const handleDeviceSelect = useCallback(async (device: IMinimalDevice) => {
		const assignee = await getAssignee(device.id)

    const newLoaner: ILoanerRecord = {
      id: `loaner-${Date.now()}`,
      assignedVehicle: device,
      loanerVehicle: null,
      issuedDate: formatDate(new Date()),
      estimatedReturnDate: '',
      assignedStaff: assignee,
      note: '',
      closedDate: '',
      isEditing: true,
    }

    setActiveLoaners((prev) => [newLoaner, ...prev])

    if (authUser) {
      await addLoaner(authUser)(newLoaner)
    }
  }, [formatDate, authUser])

	const handleAddNewLoaner = useCallback((device: IMinimalDevice) => {
		// Disable editing for previous loaners
		setActiveLoaners((prev) =>
      prev.map((loaner) => ({ ...loaner, isEditing: false }))
    )

		handleDeviceSelect(device)
  }, [handleDeviceSelect])

  const handleSave = useCallback(async (loanerId: string) => {
    // Track modified loaner to save to database
    let updatedLoaner: ILoanerRecord | undefined

    setActiveLoaners((prev) =>
      prev.map((loaner) => {
        if (loaner.id === loanerId) {
          updatedLoaner = { ...loaner, isEditing: false }
          return updatedLoaner
        }
        return loaner
      })
    )

    if (updatedLoaner && authUser) {
      await updateLoaner(authUser)(loanerId, updatedLoaner)
    }
  }, [activeLoaners, authUser])

  const handleEdit = useCallback((loanerId: string) => {
    setActiveLoaners((prev) =>
      prev.map((loaner) =>
        loaner.id === loanerId ? { ...loaner, isEditing: true } : loaner
      )
    )
  }, [])

  const handleDelete = useCallback((loanerId: string) => {
    setConfirmModal({
      open: true,
      action: 'delete',
      loanerId
    })
  }, [])

  const handleCloseLoaner = useCallback(async (loanerId: string, date: string) => {
    const loanerToClose = activeLoaners.find((loaner) => loaner.id === loanerId)

		// Move to closed
    if (loanerToClose) {
      setActiveLoaners((prev) =>
        prev.filter((loaner) => loaner.id !== loanerId)
      )

      const closed = {
        ...loanerToClose,
        closedDate: date || formatDate(new Date()), // Fallback on current date
        isEditing: false,
      }

      setClosedLoaners((prev) => [
        ...prev,
        closed,
      ])

      if (authUser) {
        await updateLoaner(authUser)(loanerId, closed)
      }
    }
  }, [formatDate, activeLoaners, authUser])

  const handleReopenLoaner = useCallback((loanerId: string) => {
    setConfirmModal({
      open: true,
      action: 'reopen',
      loanerId
    })
  }, [])

  const handleDeleteClosed = useCallback((loanerId: string) => {
    setConfirmModal({
      open: true,
      action: 'deleteClosed',
      loanerId
    })
  }, [])

  // Confirm modal with delete/reopen/delete closed actions
  const handleConfirmAction = useCallback(async () => {
    const { action, loanerId } = confirmModal

    if (!loanerId || !action) return

    switch (action) {
      case 'delete':
        setActiveLoaners((prev) => prev.filter((loaner) => loaner.id !== loanerId))
        if (authUser) {
          await deleteLoaner(authUser)(loanerId)
        }
        break

      case 'reopen':
        const loanerToReopen = closedLoaners.find((loaner) => loaner.id === loanerId)
        if (loanerToReopen) {
          setClosedLoaners((prev) => prev.filter((loaner) => loaner.id !== loanerId))
          const active = {
            ...loanerToReopen,
            closedDate: '',
            isEditing: false,
          }
          setActiveLoaners((prev) => [active, ...prev])
          if (authUser) {
            await updateLoaner(authUser)(loanerId, active)
          }
        }
        break

      case 'deleteClosed':
        setClosedLoaners((prev) => prev.filter((loaner) => loaner.id !== loanerId))
        if (authUser) {
          await deleteLoaner(authUser)(loanerId)
        }
        break
    }

    setConfirmModal({
      open: false,
      action: null,
      loanerId: null
    })
  }, [confirmModal, authUser, closedLoaners])

  const handleCloseModal = useCallback(() => {
    setConfirmModal({
      open: false,
      action: null,
      loanerId: null
    })
  }, [])

	// Update loaner
  const handleFieldChange = useCallback((
    loanerId: string,
    field: keyof ILoanerRecord,
    value: string
  ) => {
		// Update active loaner
    setActiveLoaners((prev) =>
      prev.map((loaner) =>
        loaner.id === loanerId ? { ...loaner, [field]: value } : loaner
      )
    )

		// Update closed loaner
    if (field === 'closedDate' && value.trim() !== '') {
      setTimeout(() => {
        handleCloseLoaner(loanerId, value)
      }, 100)
    }
  }, [handleCloseLoaner])

	// Assign loaner vehicle
  const handleLoanerVehicleSelect = useCallback((
    loanerId: string,
    device: IMinimalDevice
  ) => {
    setActiveLoaners((prev) =>
      prev.map((loaner) =>
        loaner.id === loanerId ? { ...loaner, loanerVehicle: device } : loaner
      )
    )
  }, [])

  return {
    activeLoaners,
    closedLoaners,
    setActiveLoaners,
    setClosedLoaners,
    isLoading,
    confirmModal,
    handleConfirmAction,
    handleCloseModal,
		handleAddNewLoaner,
    handleDeviceSelect,
    handleSave,
    handleEdit,
    handleDelete,
    handleCloseLoaner,
    handleReopenLoaner,
    handleDeleteClosed,
    handleFieldChange,
    handleLoanerVehicleSelect,
  }
}